import { createContext, useContext, useReducer, type ReactNode } from 'react'
import type { ChannelType } from '@sac/contracts'

export type StatusFilter = 'all' | 'open' | 'unassigned' | 'waiting' | 'resolved' | 'unread'

interface UiState {
  channel: ChannelType | 'todos'
  storeIds: Set<string>
  status: StatusFilter
  search: string
  advAgent: string
  advPriority: string
  advSla: boolean
  currentConversationId: string | null
  ctxTab: 'cliente' | 'produtos' | 'tickets'
  composeMode: 'reply' | 'note'
  drafts: Record<string, string>
  attachments: Record<string, string[]> // conversationId -> productIds
  ctxExpanded: boolean
  moreCust: boolean
  ticketForm: boolean
  inboxOpen: boolean
  ctxOpen: boolean
}

type Action =
  | { type: 'setChannel'; channel: ChannelType | 'todos' }
  | { type: 'toggleStore'; storeId: string }
  | { type: 'clearStores' }
  | { type: 'setStatus'; status: StatusFilter }
  | { type: 'setSearch'; search: string }
  | { type: 'setAdv'; agent?: string; priority?: string; sla?: boolean }
  | { type: 'selectConversation'; id: string }
  | { type: 'setCtxTab'; tab: UiState['ctxTab'] }
  | { type: 'setComposeMode'; mode: UiState['composeMode'] }
  | { type: 'setDraft'; conversationId: string; text: string }
  | { type: 'addAttachment'; conversationId: string; productId: string }
  | { type: 'removeAttachment'; conversationId: string; index: number }
  | { type: 'clearComposer'; conversationId: string }
  | { type: 'toggleCtxExpanded' }
  | { type: 'toggleMoreCust' }
  | { type: 'setTicketForm'; open: boolean }
  | { type: 'toggleInbox' }
  | { type: 'toggleCtx' }

const initialState: UiState = {
  channel: 'todos',
  storeIds: new Set(),
  status: 'all',
  search: '',
  advAgent: '',
  advPriority: '',
  advSla: false,
  currentConversationId: null,
  ctxTab: 'cliente',
  composeMode: 'reply',
  drafts: {},
  attachments: {},
  ctxExpanded: false,
  moreCust: false,
  ticketForm: false,
  inboxOpen: false,
  ctxOpen: false,
}

function reducer(state: UiState, action: Action): UiState {
  switch (action.type) {
    case 'setChannel':
      return { ...state, channel: action.channel }
    case 'toggleStore': {
      const next = new Set(state.storeIds)
      next.has(action.storeId) ? next.delete(action.storeId) : next.add(action.storeId)
      return { ...state, storeIds: next }
    }
    case 'clearStores':
      return { ...state, storeIds: new Set() }
    case 'setStatus':
      return { ...state, status: action.status }
    case 'setSearch':
      return { ...state, search: action.search }
    case 'setAdv':
      return {
        ...state,
        advAgent: action.agent ?? state.advAgent,
        advPriority: action.priority ?? state.advPriority,
        advSla: action.sla ?? state.advSla,
      }
    case 'selectConversation':
      return {
        ...state,
        currentConversationId: action.id,
        ctxTab: 'cliente',
        moreCust: false,
        ticketForm: false,
        composeMode: 'reply',
        inboxOpen: false,
      }
    case 'setCtxTab':
      return { ...state, ctxTab: action.tab }
    case 'setComposeMode':
      return { ...state, composeMode: action.mode }
    case 'setDraft':
      return { ...state, drafts: { ...state.drafts, [action.conversationId]: action.text } }
    case 'addAttachment': {
      const current = state.attachments[action.conversationId] ?? []
      return { ...state, attachments: { ...state.attachments, [action.conversationId]: [...current, action.productId] } }
    }
    case 'removeAttachment': {
      const current = [...(state.attachments[action.conversationId] ?? [])]
      current.splice(action.index, 1)
      return { ...state, attachments: { ...state.attachments, [action.conversationId]: current } }
    }
    case 'clearComposer':
      return {
        ...state,
        drafts: { ...state.drafts, [action.conversationId]: '' },
        attachments: { ...state.attachments, [action.conversationId]: [] },
      }
    case 'toggleCtxExpanded':
      return { ...state, ctxExpanded: !state.ctxExpanded }
    case 'toggleMoreCust':
      return { ...state, moreCust: !state.moreCust }
    case 'setTicketForm':
      return { ...state, ticketForm: action.open }
    case 'toggleInbox':
      return { ...state, inboxOpen: !state.inboxOpen }
    case 'toggleCtx':
      return { ...state, ctxOpen: !state.ctxOpen }
    default:
      return state
  }
}

const UiCtx = createContext<{ state: UiState; dispatch: React.Dispatch<Action> } | null>(null)

export function UiProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState)
  return <UiCtx.Provider value={{ state, dispatch }}>{children}</UiCtx.Provider>
}

export function useUi() {
  const ctx = useContext(UiCtx)
  if (!ctx) throw new Error('useUi must be used within UiProvider')
  return ctx
}
